/* =====================================================================
 * useLiveAgentSession — WebSocket + 오디오 녹음/재생 관리 훅
 *
 * live_agent_ws_router 의 first-message auth handshake + bidi-demo 패턴의
 * PCM 스트리밍. 오디오 재생은 bidi-demo 공식 예제와 동일한 AudioWorklet
 * ring buffer 방식.
 * ===================================================================== */

import { useCallback, useRef, useEffect } from 'react';
import i18n from '@auw/i18n';
import {
  API_BASE_URL,
  getAccessToken,
  ensureFreshToken,
  refreshAuthSession,
} from '@auw/api-client';
import {
  useLiveAgentStore,
  type LiveAgentName,
  type LiveAgentMessage,
  type LiveAgentCard,
  emitAppEvent,
} from '@auw/stores';
import { useChatStore } from '@auw/stores';
import { suggestItemTitle, createMessage, syncQueue } from '@auw/api-client';
import { getUserLocale } from '@auw/workspace-utils';
import { getEnabledPluginIds } from './livePluginsPrefetch';

const PCM_INPUT_RATE = 16000;
const PCM_OUTPUT_RATE = 24000;

function buildWsUrl(): string {
  const base = API_BASE_URL
    ? new URL(API_BASE_URL, window.location.origin)
    : new URL(window.location.origin);
  const url = new URL('/api/v1/workspaces/live-agent/ws', base);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.toString();
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Pydantic ser_json_bytes='base64' 출력은 standard 또는 URL-safe base64일 수 있음.
 * atob()는 standard base64만 허용하므로 URL-safe 문자와 패딩을 정규화한 뒤 디코딩.
 */
function base64ToArrayBuffer(raw: string): ArrayBuffer {
  let b64 = raw.replace(/-/g, '+').replace(/_/g, '/').replace(/\s/g, '');
  while (b64.length % 4 !== 0) b64 += '=';
  const binary = atob(b64);
  const buf = new ArrayBuffer(binary.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < binary.length; i++) {
    view[i] = binary.charCodeAt(i);
  }
  return buf;
}

/**
 * Gemini Live ASR이 침묵/잡음 구간에서 만들어내는 hallucination 토큰을 제거한다.
 *
 * 실측 관찰된 hallucination 패턴 (2026-04-17 기준):
 *   A. 괄호 기반 duration 마커
 *      - `[ 0m 1s200ms ]`, `( 0m 44s )`, `( 0m 1ms ]`
 *      - 여는 괄호 없이 닫는 괄호만: `0m 39s )`, `1m 6s )`
 *   B. 파이프 구분 timestamp 토큰
 *      - `| 0-20-423 |`, `| la |`, `| que |`
 *      - Gemini Live가 가끔 timestamp를 `0-20-423` 같은 3-part dash-seperated
 *        숫자 시퀀스로 뱉어낸다. 보통 `|` 로 감싸져 있다.
 *   C. 잘못 이어붙은 숫자·단위 시퀀스
 *      - `1000000000000000s4s`, `s100ms`
 *   D. 스페인어/포르투갈어 단어 hallucination (한국어/영어 발화 중에 섞임)
 *      - "que a gente tá vendo", "mañana", "está", "donde", "el", "un"
 *      - 이건 단어 자체를 지우는 게 아니라 **전체 transcript가
 *        hallucination 덩어리**일 때 통째로 버리는 편이 맞다. 뒤쪽 accumulate
 *        로직에서 처리한다.
 *
 * 처리 순서가 중요하다:
 *   1. 괄호/파이프로 감싼 duration · timestamp · 단일 단어 토큰 제거
 *   2. 나열된 duration 시퀀스 + 단독 닫힘 괄호 제거
 *   3. 하이픈-숫자 timestamp 토큰 (`0-20-423`) 제거
 *   4. 과도한 숫자 시퀀스, 잘못된 단위 연결 제거
 *   5. 고립 괄호 · 파이프 · 공백 정리
 */
function sanitizeTranscript(raw: string): string {
  if (!raw) return '';
  let t = raw;

  // 1A) 괄호/대괄호로 묶인 duration 토큰 (짝이 맞지 않는 케이스 포함).
  //     내부에 ms/s/m/h 단위가 하나 이상 포함되어 있으면 제거.
  t = t.replace(/[[(][^[\]()]*?\d+\s*(?:ms|[mhs])\b[^[\]()]*?[\])]/gi, '');
  //     여닫힘이 어긋난 경우: `[... ms)` 또는 `(... ms]`
  t = t.replace(/[[(][^[\]()]*?\d+\s*(?:ms|[mhs])\b[^\n]*?(?=\s|$)/gi, '');

  // 1B) 파이프(`|`) 구분 토큰 전체 처리.
  //     `| 0-20-423 |`, `| la |`, `| que |`, `| 0m 14s |`, `| 0-20 |` 등
  //     파이프 쌍 안쪽이 **매우 짧고**(3~20자) 숫자/하이픈 또는 2~5자
  //     소문자 단어로만 구성되면 hallucination으로 간주하고 제거.
  t = t.replace(/\|\s*[\d\s-]{1,20}\|/g, ' ');
  t = t.replace(/\|\s*[a-záéíóúñ]{1,6}\s*\|/gi, ' ');
  //     파이프 자체만 남은 짧은 조각도 날림.
  t = t.replace(/\s\|\s/g, ' ');

  // 2) **여는 괄호 없이** `0m 39s )` / `1m 6s )` / `12s ]` 처럼 duration +
  //    단독 닫힘 괄호 패턴을 반복적으로 제거.
  const DURATION_ATOM = '\\d+\\s*(?:ms|[mhs])';
  const DURATION_RUN = `(?:${DURATION_ATOM}\\b\\s*){1,5}`;
  t = t.replace(new RegExp(`${DURATION_RUN}[\\])]`, 'gi'), '');
  //    닫힘 괄호가 없더라도 "0m 39s" 같은 외톨이 duration 시퀀스가 문장 경계
  //    바로 앞에 오면 제거.
  t = t.replace(
    new RegExp(`\\s${DURATION_RUN}(?=[\\s.,!?]|$)`, 'gi'),
    ' ',
  );

  // 3) `0-20-423` / `0-20` 같은 하이픈 구분 timestamp. 일반 문장에 이런 형식은
  //    거의 나타나지 않으므로 휴리스틱으로 잘라낸다.
  t = t.replace(/\b\d{1,3}-\d{1,4}(?:-\d{1,4})?\b/g, ' ');

  // 4) 과도하게 긴 숫자 시퀀스 (8자리 이상)와 바로 뒤에 붙은 단위 토큰 제거
  t = t.replace(/\d{8,}\s*(?:ms|[mhs]\d*)?[\s\])]*/gi, '');
  //    숫자 + ms/s/m + 숫자 + s 같은 비정상 연결 (예: "s4s", "s100ms")
  t = t.replace(/\b\d+[mhs](?:\d+[mhs]?)+\b/gi, '');

  // 5) 남아있는 외톨이 여닫이 괄호 + 내부 공백/숫자만 있는 조각
  t = t.replace(/[[(]\s*\d*\s*[\])]/g, '');
  //    짝 없는 단독 닫힘 괄호(앞뒤 공백 포함)만 정리.
  t = t.replace(/\s*[\])](?=\s|$)/g, (m) => (m.endsWith(' ') ? ' ' : ''));
  //    남아있는 고립 파이프 제거
  t = t.replace(/\s*\|\s*/g, ' ');

  // 6) 연속 공백·구두점 정리
  t = t.replace(/\s+([.,!?])/g, '$1');
  t = t.replace(/\s{2,}/g, ' ').trim();
  return t;
}

/**
 * 주어진 transcript가 대부분 hallucination 잔해인지 휴리스틱으로 판별한다.
 *
 * 판정 기준 (하나라도 해당하면 drop):
 *   - sanitize 후 길이가 입력의 30% 이하로 줄었고 동시에 50자 이상 → 잔해 덩어리
 *   - 같은 2~6자 짧은 단어가 5회 이상 연속 반복 (ex: "que que que que que")
 *   - 포르투갈어/스페인어 stop-word 밀도가 매우 높음 (한국어/영어 발화 중에는
 *     정상적으로 잘 나오지 않음). "que", "la", "de", "el", "es", "un", "na"
 *     등이 단어 전체의 50% 이상을 차지할 때.
 */
function looksLikeHallucinationJunk(raw: string, cleaned: string): boolean {
  if (!cleaned) return true;
  if (raw.length >= 50 && cleaned.length <= raw.length * 0.3) return true;

  // CJK 단일문자 hallucination: ASR이 무음 구간에서 '好', '嗯', '是' 등
  // 중국어/일본어 단일 한자를 잘못 인식하는 패턴.
  const trimmed = cleaned.trim();
  if (trimmed.length <= 2 && /^[\u4e00-\u9faf\u3040-\u309f\u30a0-\u30ff]+$/.test(trimmed)) {
    return true;
  }

  const words = cleaned
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w.length > 0);
  if (words.length === 0) return true;

  let repeatRun = 1;
  for (let i = 1; i < words.length; i += 1) {
    if (words[i] === words[i - 1] && words[i].length <= 6) {
      repeatRun += 1;
      if (repeatRun >= 5) return true;
    } else {
      repeatRun = 1;
    }
  }

  const HALLUC_ES_PT = new Set([
    'que',
    'la',
    'el',
    'de',
    'es',
    'en',
    'un',
    'una',
    'los',
    'las',
    'por',
    'con',
    'para',
    'se',
    'no',
    'sí',
    'mañana',
    'gente',
    'vida',
    'donde',
    'está',
    'entonces',
    'tá',
    'né',
    'que',
    'um',
    'do',
    'da',
    'na',
    'pero',
    'todo',
    'este',
    'esa',
    'lo',
    'le',
    'su',
  ]);
  if (words.length >= 8) {
    let hits = 0;
    for (const w of words) {
      if (HALLUC_ES_PT.has(w)) hits += 1;
    }
    if (hits / words.length >= 0.5) return true;
  }
  return false;
}

/**
 * Gemini Live의 ``input/output_audio_transcription`` 이벤트는 세 가지 포맷이
 * 혼재해서 올 수 있다:
 *
 * 1. cumulative: 매 이벤트가 "지금까지 인식된 전체 텍스트"를 싣는다.
 *    (예: "안녕" → "안녕하" → "안녕하세요")
 * 2. delta: 이번 chunk에서 새로 인식된 조각만 싣는다.
 *    (예: "안녕" → "하" → "세요")
 * 3. spacing-only cumulative: partial 단계에서는 공백 없이 한글을 이어 붙이다가
 *    final 단계에서 동일한 내용을 공백이 들어간 "정제된 버전"으로 한 번 더
 *    내려보낸다. 이때 ``startsWith`` 비교는 실패하므로 naive append 로직은
 *    **같은 내용이 공백만 다른 채로 두 번 찍힌다** (현재 관찰된 버그).
 *
 * 다음 규칙으로 병합한다:
 *   - prev가 비어 있으면 chunk가 시작 값.
 *   - chunk가 prev를 include하는 cumulative면 chunk로 업데이트.
 *   - prev가 chunk를 include하면 chunk는 이미 반영된 오래된 cumulative이거나
 *     prev 일부의 재전송이므로 prev 유지.
 *   - **공백을 무시했을 때** chunk가 prev를 include하면 final-cumulative로
 *     판단해 chunk로 교체. 이 규칙이 spacing-only 중복을 잡는다.
 *   - chunk가 prev의 ``suffix``와 겹친다 (끝부분 중복) → 겹치는 만큼만 잘라
 *     뒤에 이어붙여 중복 누적 방지.
 *   - 그 외에는 delta로 간주하여 뒤에 이어붙인다. 영문/숫자 경계에서만 한 칸
 *     공백을 끼운다 (한국어는 이어붙여도 자연스럽다).
 *
 * 마지막에 한 번 더 ``sanitizeTranscript``로 이어붙인 지점에서 생길 수 있는
 * 이중 공백/구두점 앞 공백을 정리한다.
 */
/**
 * 두 문자열이 "같은 발화의 서로 다른 버전" 인지 휴리스틱으로 판단.
 *
 * Gemini Live ASR 은 partial 단계에서 한글을 공백 없이 이어 붙이다가
 * (``"우리가장먼저"``), final 단계에서 동일 내용을 공백이 들어간 정제본
 * (``"우리가 가장 먼저"``) 으로 한 번 더 보낼 수 있다. 이때 단어 경계
 * 인식이 살짝 달라지면 `normPrev === normChunk` 비교가 실패한다.
 *
 * 다만 **문장 끝 어미** 는 거의 바뀌지 않아 (`"...계셨던 건 아니죠?"`)
 * 공백 제거 후 마지막 N 글자가 일치하면 같은 발화의 재인식본으로 간주해도
 * 안전하다. 짧은 delta 조각(보통 10자 이하) 에는 적용하지 않는다.
 */
function looksLikeRetranscriptionOf(prev: string, chunk: string): boolean {
  const normPrev = prev.replace(/\s+/g, '');
  const normChunk = chunk.replace(/\s+/g, '');
  const SUFFIX_MATCH_CHARS = 12;
  if (
    normPrev.length < SUFFIX_MATCH_CHARS
    || normChunk.length < SUFFIX_MATCH_CHARS
  ) return false;
  const prevTail = normPrev.slice(-SUFFIX_MATCH_CHARS);
  const chunkTail = normChunk.slice(-SUFFIX_MATCH_CHARS);
  if (prevTail !== chunkTail) return false;

  // 덤으로 상당한 길이(>=80%)가 겹쳐야 한다 — "끝만 같은 전혀 다른 문장"
  // 을 같은 발화로 오판하지 않기 위한 sanity check.
  const minLen = Math.min(normPrev.length, normChunk.length);
  const maxLen = Math.max(normPrev.length, normChunk.length);
  return minLen / maxLen >= 0.8;
}

function mergeTranscriptChunk(prev: string, chunk: string): string {
  if (!chunk) return prev;
  if (!prev) return sanitizeTranscript(chunk);
  if (prev === chunk) return prev;

  const countWhitespace = (text: string): number =>
    (text.match(/\s/g) ?? []).length;
  const sliceByNormalizedPrefix = (text: string, normalizedPrefix: string): string => {
    let remaining = normalizedPrefix.length;
    let i = 0;
    while (i < text.length && remaining > 0) {
      if (!/\s/.test(text[i])) remaining -= 1;
      i += 1;
    }
    return text.slice(i);
  };

  if (chunk.startsWith(prev)) {
    return sanitizeTranscript(chunk);
  }
  if (prev.startsWith(chunk) || prev.endsWith(chunk)) {
    return prev;
  }

  // 공백을 무시한 cumulative 비교 — 한국어 ASR이 partial에서는 공백 없이
  // 한글을 이어붙이다가 final에서 공백이 들어간 동일 문장을 한 번 더 보내는
  // 패턴을 처리한다. normPrev가 normChunk의 prefix면 chunk를 정제된 최종본
  // 으로 간주해 prev를 대체한다.
  const normPrev = prev.replace(/\s+/g, '');
  const normChunk = chunk.replace(/\s+/g, '');
  if (normPrev && normChunk) {
    if (normPrev === normChunk) {
      // 같은 문장이라면 공백이 더 잘 보존된 쪽을 유지한다.
      return sanitizeTranscript(
        countWhitespace(chunk) >= countWhitespace(prev) ? chunk : prev,
      );
    }
    if (normChunk.startsWith(normPrev)) {
      // 새 cumulative chunk가 이전 내용의 확장판이지만 spacing이 더 나쁠 수 있다.
      // chunk의 prefix/suffix를 normalized 기준으로 분리한 뒤,
      // prefix는 더 spacing이 좋은 버전을 선택하고 suffix만 이어붙인다.
      const suffix = sliceByNormalizedPrefix(chunk, normPrev);
      const chunkPrefix = chunk.slice(0, chunk.length - suffix.length);
      const preferredPrefix =
        countWhitespace(chunkPrefix) >= countWhitespace(prev) ? chunkPrefix : prev;
      return sanitizeTranscript(preferredPrefix + suffix);
    }
    if (normPrev.startsWith(normChunk)) {
      return prev;
    }
  }

  /*
   * Gemini Live final cumulative 휴리스틱 — partial 누적본과 final
   * 정제본이 "단어 경계 재인식" 때문에 normalized 비교까지 실패했을 때,
   * 양쪽 끝 부분(suffix)이 일치하면 같은 발화의 재인식본으로 간주하고
   * chunk(정제본) 으로 교체한다. 이 경로가 없으면 아래 overlap 탐색이
   * 0 을 반환해 chunk 전체가 append 되어 "...건 아니죠? ...건 아니죠?"
   * 같은 눈에 띄는 중복이 발생한다.
   */
  if (looksLikeRetranscriptionOf(prev, chunk)) {
    return sanitizeTranscript(chunk);
  }

  // prev 끝과 chunk 시작이 겹치는 최대 길이를 찾는다 (최대 prev 길이 기준).
  const maxOverlap = Math.min(prev.length, chunk.length);
  let overlap = 0;
  for (let n = maxOverlap; n > 0; n -= 1) {
    if (prev.endsWith(chunk.slice(0, n))) {
      overlap = n;
      break;
    }
  }

  const toAppend = chunk.slice(overlap);
  if (!toAppend) return prev;

  return sanitizeTranscript(prev + toAppend);
}

const MAX_AUTO_RECONNECT = 2;
const RECONNECT_DELAY_MS = 1500;

/**
 * 에이전트 audio chunk 가 들어오지 않으면 발화 종료로 간주하는 debounce 시간.
 * Gemini Live 의 자연스러운 콤마/문장 쉼(300~500ms)보다 살짝 길게 잡아
 * 쉼 중에도 Dock 의 Rebo lift 가 깜빡이지 않도록 한다.
 */
const AGENT_SILENCE_DEBOUNCE_MS = 700;

export function useLiveAgentSession() {
  const wsRef = useRef<WebSocket | null>(null);
  const recContextRef = useRef<AudioContext | null>(null);
  const playContextRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  // 입력 recorder AudioWorklet. ScriptProcessorNode 에서 이관한 저지연 경로.
  const recorderNodeRef = useRef<AudioWorkletNode | null>(null);
  const micSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);

  const agentMsgIdRef = useRef<string | null>(null);
  const userMsgIdRef = useRef<string | null>(null);
  const connectWsRef = useRef<((sid: string) => void) | null>(null);
  const reconnectCountRef = useRef(0);
  const intentionalCloseRef = useRef(false);
  /*
   * 백엔드 keepalive 가 ``auth_expired`` 를 보낸 직후 WS 가 닫히는 경우를
   * 표시한다. 이때는 close 핸들러가 재연결 전에 ``refreshAuthSession()``
   * 으로 액세스 토큰을 강제 재발급한다 — 만료 토큰으로 재 handshake 하면
   * 4001 로 또 끊기기 때문.
   */
  const authExpiredRef = useRef(false);
  const turnCreatedRef = useRef(false);
  /*
   * 현재 턴의 message_id. AI 서버가 ``turn_start`` 프레임으로 매 턴 시작
   * 시 내려준다(첫 턴은 세션 시작 직후, 이후 턴은 직전 turn_complete 직후).
   * 그 턴에서 만들어지는 assistant 메시지에 태깅돼, 영속화 시
   * ``completedData.message_id`` 로 저장된다 → 멀티턴 활동내역/본문 매칭.
   */
  const currentTurnMsgIdRef = useRef<string | null>(null);

  /*
   * 에이전트 발화 감지 debounce.
   *
   * Gemini Live 는 ~20ms 단위의 audio chunk 를 스트리밍으로 보내므로, chunk
   * 단위로 토글하면 Dock 의 Rebo 애니메이션이 떨린다. 마지막 chunk 후
   * `SILENCE_DEBOUNCE_MS` 가 지나면 발화 종료로 간주하고 `false` 로 돌린다.
   * `turnComplete` 수신 시에는 즉시 `false`(안전망).
   */
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // outputTranscription은 누적형/델타 혼용으로 올 수 있으므로 accumulator로 관리
  const outputTxAccRef = useRef('');
  const inputTxAccRef = useRef('');

  const {
    sessionStatus,
    sessionId,
    setSessionStatus,
    setSessionId,
    addMessage,
    appendToMessageById,
    replaceMessageText,
    setActiveAgent,
    setAgentSpeaking,
    setCards,
    appendCards,
    clearSession,
  } = useLiveAgentStore();

  // ── AudioWorklet 기반 오디오 재생 (bidi-demo 패턴) ──
  const feedAudio = useCallback((pcmBuf: ArrayBuffer) => {
    if (pcmBuf.byteLength === 0) return;
    if (useLiveAgentStore.getState().muted) return;
    const node = workletNodeRef.current;
    const ctx = playContextRef.current;
    if (!node) return;
    if (ctx && ctx.state === 'suspended') ctx.resume();
    node.port.postMessage(pcmBuf);

    /*
     * 에이전트 발화 마킹: chunk 가 실제로 재생 경로로 흘러가는 순간에만 true.
     * muted 상태이거나 worklet 이 없으면 위에서 return 하므로 여기에 도달하지
     * 않는다 → Dock 의 Rebo 가 음소거 중에 괜히 올라오지 않는다.
     */
    const store = useLiveAgentStore.getState();
    if (!store.agentSpeaking) store.setAgentSpeaking(true);
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    silenceTimerRef.current = setTimeout(() => {
      silenceTimerRef.current = null;
      useLiveAgentStore.getState().setAgentSpeaking(false);
    }, AGENT_SILENCE_DEBOUNCE_MS);
  }, []);

  /*
   * ── 메시지 영속화 (통합 에이전트 syncQueue 패턴) ────────────────────
   *
   * 아직 저장 안 된 라이브 메시지를 workspace_messages 로 보낸다. 기존엔
   * createMessage 를 직접(fire-and-forget) 호출해, item 생성(syncQueue)과
   * 레이스가 나면 404 로 유실되고 재시도도 없었다 — 그래서 재접속/재클릭
   * 시 본문이 비어 시작 페이지가 떴다. 이제 통합 에이전트와 동일하게
   * syncQueue 에 enqueue 한다:
   *   - create:item 이 먼저 큐에 있으므로 FIFO 로 item 생성 후 메시지 저장
   *   - 실패 시 재시도, 소진되면 onFailure 에서 persisted 를 되돌려 다음
   *     turnComplete/stop flush 가 재시도
   * assistant 메시지는 completedData 에 {message_id(턴), agent_name} 을 담아
   * 저장한다. message_id 는 usage ledger(턴별) ↔ 활동내역 본문 매칭 키이고,
   * agent_name 은 복원용이다(loadMessages 가 metadata 는 복원 안 하지만
   * completedData 는 round-trip 한다).
   */
  const persistUnsavedLiveMessages = useCallback(() => {
    const store = useLiveAgentStore.getState();
    const cid = store.conversationId;
    if (!cid) return;
    const unsaved = store.messages.filter((m) => !m.persisted && m.text.trim());
    for (const m of unsaved) {
      // 낙관적 마킹 — 같은 메시지가 다음 flush 에서 중복 enqueue 되지 않게.
      useLiveAgentStore.getState().markMessagePersisted(m.id);
      const isUser = m.author === 'user';
      const completedData = isUser
        ? undefined
        : { message_id: m.turnMessageId ?? undefined, agent_name: m.author };
      syncQueue.enqueue({
        key: `message:live:${cid}:${m.id}`,
        execute: async () => {
          await createMessage(cid, {
            role: isUser ? 'user' : 'assistant',
            content: m.text,
            content_type: 'text',
            metadata: {
              client_message_id: m.id,
              source: 'live-agent',
              agent_name: isUser ? undefined : m.author,
              ...(completedData ? { completedData } : {}),
            },
          });
        },
        onFailure: () => {
          useLiveAgentStore.setState((s) => ({
            messages: s.messages.map((mm) =>
              mm.id === m.id ? { ...mm, persisted: false } : mm,
            ),
          }));
        },
      });
    }
  }, []);

  /*
   * ── in-band 토큰 갱신 (장시간 라이브 세션 유지) ─────────────────────
   *
   * 백엔드 keepalive 는 토큰 만료 60초 전 ``token_expiring_soon`` 을 보내고,
   * 그래도 만료되면 다음 재검증(120s 주기)에서 ``auth_expired`` 와 함께
   * 세션을 종료한다. 백엔드 프록시는 ``{type:'token_refresh'}`` in-band
   * 프레임을 받아 keepalive 의 ``token_state`` 를 갱신하므로, 여기서 액세스
   * 토큰을 재발급해 같은 WS 위로 밀어 넣으면 세션이 끊기지 않는다.
   * (기존엔 이 경로가 없어 15~20분 이상 켜 둔 라이브 세션이 조용히 죽었다.)
   *
   * ``refreshAuthSession`` 은 계정 단위로 중복 호출을 dedupe 하므로 경고가
   * 25초 간격으로 반복 수신돼도 실제 refresh 는 한 번만 나간다.
   */
  const refreshAndPushToken = useCallback(async () => {
    try {
      const refreshed = await refreshAuthSession();
      if (!refreshed) return;
      const token = getAccessToken();
      const ws = wsRef.current;
      if (token && ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'token_refresh', data: { token } }));
      }
    } catch {
      /* noop — 다음 token_expiring_soon 에서 재시도 */
    }
  }, []);

  // ── WebSocket 이벤트 처리 ──
  const handleWsMessage = useCallback(
    (ev: MessageEvent) => {
      if (ev.data instanceof ArrayBuffer) {
        feedAudio(ev.data);
        return;
      }
      if (ev.data instanceof Blob) {
        ev.data.arrayBuffer().then(feedAudio);
        return;
      }

      try {
        const event = JSON.parse(ev.data);
        const type = event.type as string;

        if (type === 'auth_ok' || type === 'session_ready') {
          if (type === 'session_ready') {
            const plugins: string[] = event.data?.connected_plugins ?? [];
            if (plugins.length > 0) {
              useLiveAgentStore.getState().setConnectedPlugins(plugins);
            }
          }
          setSessionStatus('active');
          return;
        }

        if (type === 'server_ping' || type === 'token_expiring_soon') {
          const ws = wsRef.current;
          if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'pong' }));
          }
          if (type === 'token_expiring_soon') {
            // 만료 전에 토큰을 재발급해 in-band 로 갱신 — 세션 유지.
            void refreshAndPushToken();
          }
          return;
        }

        if (type === 'auth_expired') {
          /*
           * 토큰 만료로 백엔드가 곧 WS 를 닫는다. 'error' 로 죽이지 않고
           * 플래그만 세워 둔다 — onclose 의 자동 재연결 경로가 (sessionStatus
           * 가 여전히 'active' 이므로) 그대로 동작하고, 재연결 전에
           * 토큰을 강제 재발급한다. 사용자 입장에서는 끊김 없이 이어진다.
           */
          authExpiredRef.current = true;
          return;
        }

        if (type === 'error') {
          setSessionStatus('error');
          return;
        }

        /*
         * ── 턴 경계 신호 (멀티턴 message_id) ────────────────────────────
         * AI 서버가 매 턴 시작 시 그 턴의 message_id 를 내려준다. 이후
         * turnComplete 까지 만들어지는 assistant 메시지를 이 id 로 태깅해,
         * 영속화 시 completedData.message_id 로 저장한다(usage ledger 와
         * 동일 키). 첫 턴 id 는 세션 시작 직후 1회, 이후엔 직전 턴 종료
         * 직후에 도착한다.
         */
        if (type === 'turn_start') {
          const mid = event.data?.message_id;
          if (typeof mid === 'string' && mid) {
            currentTurnMsgIdRef.current = mid;
          }
          return;
        }

        /*
         * ── 카드 부수 채널 (lens-mosaic ``find_items`` 패턴) ─────────────
         *
         * 도구 호출 결과 / 검색 출처를 음성 발화와 별개로 카드 형태로 화면에
         * 띄우기 위해 서버가 보내는 메시지. 음성 모델은 자연스러운 한국어
         * 1~3문장만 발화하고, 정확한 제목/저자/URL 같은 메타데이터는 이쪽
         * 채널로만 흐른다.
         *
         *   - ``cards.snapshot``: 현재 stash 전체 — 새 연결 직후 자동, 또는
         *     클라이언트가 ``request_snapshot`` 으로 수동 요청한 응답.
         *     ``setCards(items)`` 로 통째 교체.
         *   - ``cards.update``: 추가/갱신 페이로드. ``appendCards`` 가 id
         *     기준 dedupe 후 끝에 push (같은 id 면 덮어쓰기).
         *
         * 잘못된 페이로드(items 가 array 아님)는 조용히 무시한다.
         */
        if (type === 'cards.snapshot' || type === 'cards.update') {
          const items = event.data?.items;
          if (!Array.isArray(items)) return;
          const cards = items.filter(
            (c): c is LiveAgentCard =>
              c
              && typeof c === 'object'
              && typeof c.id === 'string'
              && typeof c.kind === 'string'
              && typeof c.title === 'string'
              // ``source_label`` 은 링크 카드에만 있고 본문 카드(content)는
              // 비어 있으므로 optional 로 검증한다.
              && (c.source_label == null || typeof c.source_label === 'string'),
          );
          if (type === 'cards.snapshot') {
            /*
             * 빈 snapshot 은 무시한다. prod 의 AI 서비스는 ECS 멀티 태스크로
             * 돌고 카드 stash 는 프로세스-로컬 메모리라, 재연결이 다른
             * 태스크에 떨어지면 항상 ``items=[]`` snapshot 이 내려온다.
             * 이걸 ``setCards([])`` 로 반영하면 이미 메시지에 붙어 렌더된
             * 카드까지 전부 지워져 "화면을 참고하라는데 블록이 없는" 증상이
             * 된다. 카드의 source of truth 는 어차피 이 스토어(메시지에
             * absorb 된 카드 + pendingTurnCards)이므로, 비어있지 않은
             * snapshot(실제 복원 데이터)만 반영한다.
             */
            if (cards.length > 0) setCards(cards);
          } else {
            if (cards.length > 0) appendCards(cards);
          }
          return;
        }

        reconnectCountRef.current = 0;

        // ADK Event (camelCase aliases)
        const rawAuthor: string = event.author ?? 'Rebo';
        const author = rawAuthor as LiveAgentName;
        const turnComplete = event.turnComplete ?? false;

        if (rawAuthor && rawAuthor !== 'user') {
          setActiveAgent(author);
        }

        // function_call 기반 dock 활성화 (싱글 에이전트에서 도구 사용 시)
        const parts = event.content?.parts as Array<Record<string, unknown>> | undefined;
        if (parts) {
          for (const p of parts) {
            const fc = (p.functionCall ?? p.function_call) as Record<string, unknown> | undefined;
            if (fc?.name === 'google_search') {
              setActiveAgent('WebAgent');
            }
          }
        }

        // ── content.parts 처리 (오디오만 — 텍스트는 transcription 이벤트 사용) ──
        // lens-mosaic 패턴: content.parts.text는 모델 내부 reasoning을 포함할 수
        // 있으므로 렌더링하지 않는다. 에이전트 버블은 outputTranscription만 사용.
        const content = event.content;
        if (content?.parts) {
          for (const part of content.parts) {
            const inlineData = part.inlineData ?? part.inline_data;
            if (inlineData?.data) {
              try {
                const pcmBuf = base64ToArrayBuffer(inlineData.data);
                feedAudio(pcmBuf);
              } catch (e) {
                console.warn('[LiveAgent] audio decode error:', e);
              }
            }
          }
        }

        /*
         * Gemini Live transcription 이벤트는 두 가지 형태로 들어온다
         * (adk_patches.py 의 yield 순서 기준):
         *
         *   1) Partial delta    — outputTranscription.text = 증분 조각
         *                          partial = true, finished = false
         *   2) Final cumulative — outputTranscription.text = 백엔드가 누적한
         *                          정제본 전체 (공백/띄어쓰기까지 보정)
         *                          partial = false, finished = true
         *
         * Final 이벤트를 "또 다른 델타" 로 취급해서 merge 하면, partial 누적본
         * 과 final 정제본 사이의 단어 경계 재인식 차이 때문에 정제본 전체가
         * tail 로 append 돼 "...계셨던 건 아니죠? ...계셨던 건 아니죠?" 식의
         * 중복이 생긴다. 따라서 final (partial === false) 이벤트가 오면
         * accumulator 를 통째로 정제본으로 교체한다.
         */
        const isFinalTranscript =
          event.partial === false || event.partial === 'false';

        // ── 입력 음성 전사 (누적형/델타 혼용 accumulator + hallucination sanitize) ──
        const inputTx = event.inputTranscription;
        const rawInputText = typeof inputTx === 'string' ? inputTx : inputTx?.text ?? '';
        const inputFinished = typeof inputTx === 'object' && inputTx?.finished === true;
        const sanitizedInput = sanitizeTranscript(rawInputText);
        if (sanitizedInput) {
          if (looksLikeHallucinationJunk(rawInputText, sanitizedInput)) {
            // 덩어리째 할루시네이션이면 이번 이벤트만 버리고 이전 누적은 유지.
          } else {
            if (isFinalTranscript && inputFinished) {
              inputTxAccRef.current = sanitizedInput;
            } else {
              inputTxAccRef.current = mergeTranscriptChunk(
                inputTxAccRef.current,
                sanitizedInput,
              );
            }
            const next = inputTxAccRef.current;
            if (userMsgIdRef.current) {
              replaceMessageText(userMsgIdRef.current, next);
            } else {
              const id = generateId();
              userMsgIdRef.current = id;
              /* 새 user 발화 시 — `LiveAgentPage` 가 그 시점의 latestUserId
               * 를 baseline 으로 잡고, addMessage 후 store update 로 latestUserId
               * 가 바뀌면 useLayoutEffect 가 promote → align 한다.  emit 은
               * store update *직전* 에 발행해야 baseline trick 이 동작한다. */
              emitAppEvent('agent:anchorLatestUser');
              addMessage({
                id,
                author: 'user',
                text: next,
                timestamp: Date.now(),
                transcript: next,
              });
            }
          }
        }

        /*
         * ── 출력(에이전트) 음성 전사 — lens-mosaic 스타일 ──────────────
         *
         * Gemini Live 는 output_transcription 을 다음 두 단계로 보낸다:
         *   1) Partial   : raw ASR 조각 (한국어는 공백 없이 글자만)
         *   2) Finished  : 띄어쓰기/구두점까지 정제된 전체 문장
         *
         * partial 을 실시간 버블에 흘리면 "우리가장먼저" 처럼 공백 없는 상태로
         * 보이다가 finished 시점에 한 번에 공백 들어간 버전으로 교체돼 UI 가
         * 깜빡인다. lens-mosaic 처럼 **partial 은 UI 에 반영하지 않고**
         * (`outputAudioTranscription` 의 audio 재생은 계속 진행)
         * finished 이벤트의 정제본만 에이전트 버블로 만든다.
         *
         * 이렇게 하면:
         *  - 에이전트 오디오는 실시간으로 들리고 Dock 의 Rebo 도 정상 동작
         *  - 에이전트 버블은 답변이 끝나는 시점에 한 번에 깔끔한 문장으로 등장
         *  - `mergeTranscriptChunk` / `looksLikeRetranscriptionOf` 같은
         *    휴리스틱이 출력 경로에서는 돌 필요 자체가 없음
         *
         * 중간 flush(`turn_complete` 만 오고 finished 가 없는 경로) 에서는
         * 기존 `outputTxAccRef.current` 가 비어 있어도 finished 이벤트가
         * 대체로 들어오므로 버블이 누락되지 않는다. 그럼에도 안전장치로
         * `turn_complete` 블록에서 accumulator 의 내용을 flush 한다(아래
         * 참조).
         */
        const outputTx = event.outputTranscription;
        const rawOutputText = typeof outputTx === 'string' ? outputTx : outputTx?.text ?? '';
        const outputFinished = typeof outputTx === 'object' && outputTx?.finished === true;
        const sanitizedOutput = sanitizeTranscript(rawOutputText);
        if (sanitizedOutput && !looksLikeHallucinationJunk(rawOutputText, sanitizedOutput)) {
          if (isFinalTranscript && outputFinished) {
            // 정제된 최종본이 도착 — 에이전트 버블을 여기서 처음 만들거나
            // (아직 없으면), 이미 있으면 텍스트만 교체한다.
            outputTxAccRef.current = sanitizedOutput;
            if (agentMsgIdRef.current) {
              replaceMessageText(agentMsgIdRef.current, sanitizedOutput);
            } else {
              const id = generateId();
              agentMsgIdRef.current = id;
              addMessage({
                id,
                author,
                text: sanitizedOutput,
                timestamp: Date.now(),
                transcript: sanitizedOutput,
                turnMessageId: currentTurnMsgIdRef.current ?? undefined,
              });
            }
          } else {
            // Partial delta — UI 에 반영하지 않는다. 안전장치로 accumulator
            // 에는 쌓아 두어, 혹시 finished 없이 turn_complete 만 오는 예외
            // 경로에서 그때까지 모은 raw 조각이라도 flush 할 수 있게 한다.
            outputTxAccRef.current = mergeTranscriptChunk(
              outputTxAccRef.current,
              sanitizedOutput,
            );
          }
        }

        if (turnComplete) {
          /*
           * 안전장치: finished=True 이벤트가 어떤 이유로 누락된 채 turn 이
           * 끝나버렸는데 partial accumulator 에 내용이 남아 있다면, raw
           * 조각본이라도 에이전트 버블로 flush 한다. 공백이 빠진 문장이
           * 되더라도 "에이전트가 분명 말했는데 버블이 없음" 보다는 낫다.
           * 정상 경로에서는 finished 이벤트 처리 시점에 이미 버블이 생성돼
           * `agentMsgIdRef.current` 가 세팅되므로 이 블록은 드물게만 탄다.
           */
          if (!agentMsgIdRef.current && outputTxAccRef.current) {
            const fallbackText = outputTxAccRef.current;
            const id = generateId();
            agentMsgIdRef.current = id;
            addMessage({
              id,
              author,
              text: fallbackText,
              timestamp: Date.now(),
              transcript: fallbackText,
              turnMessageId: currentTurnMsgIdRef.current ?? undefined,
            });
          }

          // 턴 종료 시 발화 플래그를 즉시 내려 Dock 의 Rebo 가 지체 없이
          // 기본 위치로 복귀하도록 한다. debounce 타이머가 남아있다면 제거.
          if (silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
          }
          setAgentSpeaking(false);

          agentMsgIdRef.current = null;
          userMsgIdRef.current = null;
          outputTxAccRef.current = '';
          inputTxAccRef.current = '';

          // 첫 턴 완료 시 제목 추천 (대화 item 은 세션 시작 시점에 이미
          // 선생성됨 — 아래 startSession 참조). 사이드바엔 fallback 제목으로
          // 떠 있고, 첫 유저 발화가 모이면 LLM 추천 제목으로 바꿔준다.
          if (!turnCreatedRef.current) {
            turnCreatedRef.current = true;
            const { messages: allMsgs, conversationId: convId } = useLiveAgentStore.getState();
            if (convId && allMsgs.length > 0) {
              const firstUserMsg = allMsgs.find((m) => m.author === 'user');
              const raw = (firstUserMsg?.text ?? '').replace(/\s+/g, ' ').trim();
              if (raw) {
                suggestItemTitle(convId, { content: raw }).then((res) => {
                  const suggested = typeof res.title === 'string' ? res.title.trim() : '';
                  if (!suggested) return;
                  useChatStore.getState().renameConversation(convId, suggested);
                }).catch(() => {});
              }
            }
          }

          // 메시지 영속화 — 통합 에이전트와 동일하게 syncQueue(FIFO+재시도)로
          // 보낸다. create:item 이 먼저 enqueue 돼 있어 순서가 보장된다.
          persistUnsavedLiveMessages();
        }
      } catch {
        // non-JSON ignored
      }
    },
    [
      addMessage,
      appendToMessageById,
      replaceMessageText,
      feedAudio,
      setActiveAgent,
      setAgentSpeaking,
      setSessionStatus,
      setCards,
      appendCards,
      persistUnsavedLiveMessages,
      refreshAndPushToken,
    ],
  );

  // ── 마이크 중지 ──
  const stopMicrophone = useCallback(() => {
    // worklet node 를 먼저 메시지 끊고 disconnect — 노드가 떠있는 동안
    // postMessage 가 main thread 에 계속 도착하면 세션 뒤처리가 꼬인다.
    const recorder = recorderNodeRef.current;
    if (recorder) {
      recorder.port.onmessage = null;
      try {
        recorder.port.postMessage({ command: 'flush' });
      } catch {
        /* noop */
      }
      recorder.disconnect();
      recorderNodeRef.current = null;
    }
    micSourceRef.current?.disconnect();
    micSourceRef.current = null;
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current = null;
  }, []);

  // ── WebSocket 연결 (재사용 가능) ──
  const connectWs = useCallback(
    (sid: string) => {
      const ws = new WebSocket(buildWsUrl());
      ws.binaryType = 'arraybuffer';
      wsRef.current = ws;

      ws.onopen = async () => {
        /*
         * WS 는 HTTP 와 달리 401 재시도가 불가능하므로 handshake 전에 토큰
         * 신선도를 보장한다 (만료 60초 이내면 자동 refresh). 재연결 경로
         * (auth_expired 포함) 에서도 이 한 줄이 만료 토큰 재사용을 막는다.
         */
        const token = (await ensureFreshToken().catch(() => '')) || getAccessToken();
        const { voiceName, messages: storeMessages, conversationId } = useLiveAgentStore.getState();
        const authData: Record<string, unknown> = {
          token,
          session_id: sid,
          // 라이브 세션이 속한 대화 item — 백엔드 프록시가 usage ledger /
          // usage_updated 이벤트에 이 item_id 를 기록한다(턴별 활동내역).
          ...(conversationId ? { item_id: conversationId } : {}),
          voice_name: voiceName,
          // 통합 에이전트 streamAgent와 동일한 user_locale 스키마로 전달.
          // 백엔드 프록시 → AI 서버 → Live Agent 시스템 프롬프트의
          // "## User Context" 블록으로 렌더된다.
          user_locale: getUserLocale(),
        };

        /*
         * ── 멀티턴 히스토리 주입 (Dock "중지"→"대화하기" 재개 시 기억 유지) ──
         *
         * 라이브 에이전트 WS 는 연결마다 새 `InMemorySessionService` + 새 Runner
         * 를 만들기 때문에 `session_id` 문자열을 재사용해도 AI 측 대화
         * 컨텍스트는 매번 빈 상태로 시작된다. 기억을 이어주려면 프론트가
         * 이 시점에 과거 턴을 명시적으로 전달해야 한다.
         *
         * 수집 규칙:
         *   - `liveAgentStore.messages` 에서 `text` 가 실제로 있는 턴만
         *   - 역할 매핑: `author === 'user'` → `'user'`, 그 외(에이전트 이름) →
         *     `'assistant'` (AI 백엔드에서 `role='model'` 로 재매핑)
         *   - 최근 `HISTORY_MAX_MESSAGES` 개만 잘라 전송해 토큰 폭발 방지
         *
         * 첫 세션(startSession) 에서는 `clearSession` 으로 messages 가 비어
         * 있으므로 자연스럽게 빈 배열이 된다. 재개(resumeSession) / 사이드바
         * 대화 복원 케이스에서만 실제로 페이로드가 실린다.
         */
        const HISTORY_MAX_MESSAGES = 60;
        const recent = (storeMessages as LiveAgentMessage[])
          .filter((m) => typeof m.text === 'string' && m.text.trim().length > 0)
          .slice(-HISTORY_MAX_MESSAGES);
        if (recent.length > 0) {
          authData.history = recent.map((m) => ({
            role: m.author === 'user' ? 'user' : 'assistant',
            text: m.text,
          }));
        }

        /*
         * enabled_plugins 은 핸드셰이크 critical path 바깥에서 prefetch 된다.
         * 캐시 hit 이면 ~0ms, miss 여도 최대 500ms 만 기다리고 빈 배열
         * fallback → auth 송신 지연을 사실상 제거한다. 실제 연결된 플러그인
         * 목록은 이후 서버가 내려주는 ``session_ready.connected_plugins`` 가
         * 권위를 가지므로 여기서 빈 배열이어도 세션은 정상 작동한다.
         */
        const connected = await getEnabledPluginIds();
        if (connected.length > 0) {
          authData.enabled_plugins = connected;
        }

        if (ws.readyState !== WebSocket.OPEN) return;
        try {
          ws.send(JSON.stringify({ type: 'auth', data: authData }));
        } catch {
          /* noop — ws 가 race 로 닫혔을 수 있다 */
        }
      };

      ws.onmessage = handleWsMessage;

      ws.onerror = () => {
        wsRef.current = null;
        stopMicrophone();
      };

      ws.onclose = () => {
        wsRef.current = null;
        stopMicrophone();

        if (intentionalCloseRef.current) return;

        const wasAuthExpired = authExpiredRef.current;
        authExpiredRef.current = false;

        const { sessionStatus: cur } = useLiveAgentStore.getState();
        if (
          (cur === 'active' || cur === 'connecting') &&
          reconnectCountRef.current < MAX_AUTO_RECONNECT
        ) {
          reconnectCountRef.current += 1;
          console.info(
            `[LiveAgent] auto-reconnect ${reconnectCountRef.current}/${MAX_AUTO_RECONNECT}` +
              (wasAuthExpired ? ' (auth_expired → token refresh)' : ''),
          );
          setSessionStatus('connecting');
          if (wasAuthExpired) {
            /*
             * auth_expired 로 닫힌 경우 만료 토큰으로 재 handshake 하면 또
             * 4001 로 끊긴다. 먼저 refresh 를 끝내고(실패해도 onopen 의
             * ensureFreshToken 이 한 번 더 시도) 재연결한다.
             */
            void refreshAuthSession()
              .catch(() => false)
              .finally(() => {
                setTimeout(() => connectWsRef.current?.(sid), RECONNECT_DELAY_MS);
              });
          } else {
            setTimeout(() => connectWsRef.current?.(sid), RECONNECT_DELAY_MS);
          }
          return;
        }

        if (cur === 'active' || cur === 'connecting') {
          setSessionStatus('disconnected');
        }
      };
    },
    [handleWsMessage, stopMicrophone, setSessionStatus],
  );
  connectWsRef.current = connectWs;

  /*
   * 오디오 컨텍스트 초기화.
   *
   * 재개(resumeSession) 경로에서는 이전 ctx 가 여전히 살아있을 수 있다.
   * 브라우저마다 활성 AudioContext 개수 상한(Chrome ~6) 이 있어 await 없이
   * 교체하면 새 ctx 가 ``suspended`` 상태로 시작되는 경우가 있다. 특히
   * ``recContext`` 가 suspended 면 마이크 recorder ``AudioWorkletNode`` 가
   * ``process()`` 콜백을 받지 못해 PCM 이 서버로 흐르지 않고, 서버는 입력이
   * 없으니 응답도 내지 않는다 → "재활성화 후 무응답".
   *
   * 이를 예방하기 위해
   *   1) 이전 ctx 를 ``await close()`` 로 완전히 해제한 뒤에 새 ctx 를 생성
   *   2) 신규 ctx 가 suspended 로 시작되면 ``resume()`` 을 시도
   *   3) 실패/에러는 전부 swallow 해 세션 시작을 막지 않음
   */
  const initAudioContexts = useCallback(async () => {
    const oldRec = recContextRef.current;
    if (oldRec) {
      recContextRef.current = null;
      try {
        if (oldRec.state !== 'closed') await oldRec.close();
      } catch {
        /* noop */
      }
    }
    const recCtx = new AudioContext({ sampleRate: PCM_INPUT_RATE });
    recContextRef.current = recCtx;
    if (recCtx.state === 'suspended') {
      try {
        await recCtx.resume();
      } catch {
        /* noop */
      }
    }

    workletNodeRef.current?.disconnect();
    workletNodeRef.current = null;

    const oldPlay = playContextRef.current;
    if (oldPlay) {
      playContextRef.current = null;
      try {
        if (oldPlay.state !== 'closed') await oldPlay.close();
      } catch {
        /* noop */
      }
    }
    const playCtx = new AudioContext({ sampleRate: PCM_OUTPUT_RATE });
    playContextRef.current = playCtx;
    if (playCtx.state === 'suspended') {
      try {
        await playCtx.resume();
      } catch {
        /* noop */
      }
    }

    try {
      await playCtx.audioWorklet.addModule('/pcm-player-processor.js');
      const playerNode = new AudioWorkletNode(playCtx, 'pcm-player-processor');
      playerNode.connect(playCtx.destination);
      workletNodeRef.current = playerNode;
      // 현재 store playbackRate 를 worklet 에 즉시 반영. 이후 변경은 별도
      // useEffect (아래 ``setPlaybackRate sync``) 가 구독해서 push 한다.
      try {
        const initialRate = useLiveAgentStore.getState().playbackRate;
        if (Number.isFinite(initialRate) && initialRate !== 1.0) {
          playerNode.port.postMessage({
            command: 'setPlaybackRate',
            rate: initialRate,
          });
        }
      } catch {
        /* noop */
      }
    } catch (e) {
      console.error('[LiveAgent] AudioWorklet load failed:', e);
    }

    // 입력 recorder worklet 은 `startMicrophone()` 안에서 recContext 에 attach
    // 된다. 여기서 모듈을 미리 addModule 해 두면 첫 마이크 연결 시 파싱
    // 지연을 제거할 수 있다.
    try {
      await recCtx.audioWorklet.addModule('/pcm-recorder-processor.js');
    } catch (e) {
      console.error('[LiveAgent] recorder worklet load failed:', e);
    }
  }, []);

  // ── 연결/오디오 정리 (메시지 유지) ──
  const teardownConnection = useCallback(() => {
    intentionalCloseRef.current = true;
    stopMicrophone();
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }
    recContextRef.current?.close();
    recContextRef.current = null;
    workletNodeRef.current?.port.postMessage({ command: 'endOfAudio' });
    workletNodeRef.current?.disconnect();
    workletNodeRef.current = null;
    playContextRef.current?.close();
    playContextRef.current = null;
    agentMsgIdRef.current = null;
    userMsgIdRef.current = null;

    // 발화 debounce 타이머 제거 + 플래그 리셋 (Dock Rebo 즉시 복귀)
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    setAgentSpeaking(false);
  }, [stopMicrophone, setAgentSpeaking]);

  // ── 세션 시작 (처음 — 메시지 초기화) ──
  const startSession = useCallback(async () => {
    intentionalCloseRef.current = false;

    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }

    clearSession();
    agentMsgIdRef.current = null;
    userMsgIdRef.current = null;
    outputTxAccRef.current = '';
    inputTxAccRef.current = '';
    reconnectCountRef.current = 0;
    turnCreatedRef.current = false;
    currentTurnMsgIdRef.current = null;
    setSessionStatus('connecting');

    const newSessionId = `live-${generateId()}`;
    setSessionId(newSessionId);

    // 세션 시작 시점에 대화 item 을 선생성한다(이전엔 첫 턴 완료 후 lazy
    // 생성). 이래야 (1) WS 핸드셰이크에 item_id 를 실어 보내 백엔드가
    // usage ledger 를 이 item 으로 기록하고(턴별 활동내역 + 본문 매칭),
    // (2) 메시지 영속화가 존재하는 item 에 매달려 레이스 없이 저장된다.
    // create:item 은 syncQueue 에 enqueue 되며, 제목은 첫 턴 완료 시 LLM
    // 추천으로 교체된다.
    const convId = useChatStore.getState().createConversation({
      mode: 'live-agent',
      title: i18n.t('common:liveAgent.defaultTitle'),
      startPagePending: false,
    });
    useLiveAgentStore.getState().setConversationId(convId);

    await initAudioContexts();
    connectWs(newSessionId);
  }, [clearSession, connectWs, initAudioContexts, setSessionId, setSessionStatus]);

  // ── 세션 중지 (Dock "대화 중지" — 메시지 유지) ──
  const stopSession = useCallback(() => {
    teardownConnection();
    // 마지막 턴 메시지가 turnComplete 없이 끊겼을 수 있으니 한 번 더 flush.
    persistUnsavedLiveMessages();
    setSessionStatus('disconnected');
  }, [teardownConnection, persistUnsavedLiveMessages, setSessionStatus]);

  // ── 세션 재개 (Dock "대화하기" — 메시지 유지, 같은 세션 ID로 재연결) ──
  const resumeSession = useCallback(async () => {
    intentionalCloseRef.current = false;
    reconnectCountRef.current = 0;
    agentMsgIdRef.current = null;
    userMsgIdRef.current = null;
    outputTxAccRef.current = '';
    inputTxAccRef.current = '';
    // 재접속하면 AI 가 새 nonce 로 turn_start 를 다시 내려준다. 직전 세션의
    // 턴 id 를 잠깐이라도 재사용하지 않도록 초기화.
    currentTurnMsgIdRef.current = null;
    setSessionStatus('connecting');

    const existingId = useLiveAgentStore.getState().sessionId;
    const sid = existingId || `live-${generateId()}`;
    if (!existingId) setSessionId(sid);

    await initAudioContexts();
    connectWs(sid);
  }, [connectWs, initAudioContexts, setSessionId, setSessionStatus]);

  // ── 마이크 시작 (lens-mosaic 패턴: 모든 PCM을 그대로 전송, 서버 VAD에 위임) ──
  const startMicrophone = useCallback(async () => {
    const ws = wsRef.current;
    const ctx = recContextRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !ctx) return;

    // 페이지 복귀 / 탭 비활성 등으로 ctx 가 suspended 로 돌아간 경우를 대비해
    // 마이크 파이프라인을 붙이기 직전 한 번 더 resume 을 시도한다. suspended
    // 상태면 recorder worklet ``process()`` 가 콜백되지 않아 서버로 PCM 이
    // 나가지 않고 체감상 "반응 없음" 증상이 된다.
    if (ctx.state === 'suspended') {
      try {
        await ctx.resume();
      } catch {
        /* noop */
      }
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: PCM_INPUT_RATE,
          channelCount: 1,
          echoCancellation: true,
        },
      });
      mediaStreamRef.current = stream;

      /*
       * PCM recorder AudioWorklet.
       *
       * 모듈은 `initAudioContexts` 에서 이미 addModule 되어 있다. worklet 은
       * 내부에서 128-sample 블록을 받아 Int16 으로 변환·누적한 뒤 chunk
       * 단위(기본 32ms @16kHz)로 main thread 에 postMessage 한다. main
       * thread 는 그 ArrayBuffer 를 그대로 WS 에 send — 복사 0 회.
       *
       * recorder 는 스피커로 출력되어선 안되므로 `ctx.destination` 에
       * 연결하지 않는다. Chrome 은 destination 없이도 AudioWorklet 이 잘
       * 동작한다 (MediaStreamSource 가 graph root 역할). Safari/Firefox 와
       * 호환을 위해 안전망으로 gain=0 sink 를 함께 둔다.
       */
      let recorder: AudioWorkletNode;
      try {
        recorder = new AudioWorkletNode(ctx, 'pcm-recorder-processor', {
          numberOfInputs: 1,
          numberOfOutputs: 0,
          channelCount: 1,
        });
      } catch (e) {
        // 모듈이 아직 로드되지 않은 예외 케이스를 방어적으로 복구.
        console.warn('[LiveAgent] recorder worklet not ready, re-loading:', e);
        await ctx.audioWorklet.addModule('/pcm-recorder-processor.js');
        recorder = new AudioWorkletNode(ctx, 'pcm-recorder-processor', {
          numberOfInputs: 1,
          numberOfOutputs: 0,
          channelCount: 1,
        });
      }
      recorderNodeRef.current = recorder;

      recorder.port.onmessage = (ev) => {
        const activeWs = wsRef.current;
        if (!activeWs || activeWs.readyState !== WebSocket.OPEN) return;
        const data = ev.data;
        if (data instanceof ArrayBuffer) {
          try {
            activeWs.send(data);
          } catch {
            /* noop — ws 가 race 로 닫힘 */
          }
        }
      };

      const source = ctx.createMediaStreamSource(stream);
      micSourceRef.current = source;
      source.connect(recorder);
    } catch (err) {
      console.error('[LiveAgent] Microphone access failed:', err);
    }
  }, []);

  // ── 텍스트 전송 ──
  const sendText = useCallback(
    (text: string) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      ws.send(JSON.stringify({ type: 'text', text }));

      /* `agent:anchorLatestUser` 를 store update 직전에 emit 해 baseline
       * 을 이전 latestUserId 로 고정. 자세한 설명은 transcript 경로 참조. */
      emitAppEvent('agent:anchorLatestUser');
      addMessage({
        id: generateId(),
        author: 'user',
        text,
        timestamp: Date.now(),
      });
    },
    [addMessage],
  );

  // ── Store에 함수 등록 ──
  const setSendTextFn = useLiveAgentStore((s) => s.setSendTextFn);
  const setStopSessionFn = useLiveAgentStore((s) => s.setStopSessionFn);
  const setResumeSessionFn = useLiveAgentStore((s) => s.setResumeSessionFn);

  useEffect(() => {
    setSendTextFn(sendText);
    return () => setSendTextFn(null);
  }, [sendText, setSendTextFn]);

  useEffect(() => {
    setStopSessionFn(stopSession);
    return () => setStopSessionFn(null);
  }, [stopSession, setStopSessionFn]);

  useEffect(() => {
    setResumeSessionFn(resumeSession);
    return () => setResumeSessionFn(null);
  }, [resumeSession, setResumeSessionFn]);

  // ── 컴포넌트 언마운트 시 완전 정리 (페이지 이동 등) ──
  useEffect(() => {
    return () => {
      const { sessionStatus: cur } = useLiveAgentStore.getState();
      teardownConnection();
      if (cur === 'active' || cur === 'connecting') {
        useLiveAgentStore.getState().setSessionStatus('disconnected');
      }
    };
  }, [teardownConnection]);

  /*
   * 발화 속도 (playbackRate) sync.
   *
   * 사용자가 설정 모달에서 slider 를 움직이면 store 의 playbackRate 가 바뀐
   * 다.  현재 살아있는 PCM worklet 에 즉시 반영되도록 변경 시점마다
   * ``setPlaybackRate`` 메시지를 push 한다.  worklet 측이 0.5 ~ 2.0 로
   * clamp 하므로 여기서는 raw 값 전달.  worklet 이 아직 초기화되지 않은
   * 상태에서는 ``initAudioContexts`` 가 store 의 현재값을 한 번 push 하므로
   * 안전.
   */
  useEffect(() => {
    // ``subscribeWithSelector`` 미들웨어 없는 default subscribe 는 listener
    // 에 prevState 를 안 넘긴다. 자체 ref 로 직전값 추적해 변경 시에만 push.
    let prevRate = useLiveAgentStore.getState().playbackRate;
    const unsubscribe = useLiveAgentStore.subscribe((state) => {
      if (state.playbackRate === prevRate) return;
      prevRate = state.playbackRate;
      const node = workletNodeRef.current;
      if (!node) return;
      try {
        node.port.postMessage({
          command: 'setPlaybackRate',
          rate: state.playbackRate,
        });
      } catch {
        /* noop — worklet 이 정리 중일 수 있음 */
      }
    });
    return () => {
      unsubscribe();
    };
  }, []);

  /*
   * 탭 가시성 복귀 시 AudioContext 되살리기.
   *
   * 브라우저는 오래된 탭을 백그라운드로 보내면서 AudioContext 를 종종
   * ``suspended`` / ``interrupted`` 상태로 전환한다. 돌아왔을 때 자동 resume
   * 되는 브라우저도 있지만 Safari/모바일 Chrome 처럼 사용자 제스처 없이는
   * 깨우지 않는 환경도 있다. 이 effect 는 visibility 가 복귀하면 현재
   * 살아있는 ctx 두 개를 best-effort 로 resume 해, 페이지 이동 후 Live 세션을
   * 재개했을 때 마이크/스피커 파이프라인이 조용히 죽어있지 않도록 한다.
   */
  useEffect(() => {
    const resumeLive = () => {
      if (document.visibilityState !== 'visible') return;
      const rec = recContextRef.current;
      const play = playContextRef.current;
      if (rec && rec.state === 'suspended') rec.resume().catch(() => {});
      if (play && play.state === 'suspended') play.resume().catch(() => {});
    };
    document.addEventListener('visibilitychange', resumeLive);
    window.addEventListener('focus', resumeLive);
    return () => {
      document.removeEventListener('visibilitychange', resumeLive);
      window.removeEventListener('focus', resumeLive);
    };
  }, []);

  return {
    sessionStatus,
    sessionId,
    startSession,
    stopSession,
    resumeSession,
    startMicrophone,
    stopMicrophone,
    sendText,
  };
}
